import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Lead } from '../types';
import { getApiUrl } from '../config/api';

export function LeadsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const filter = searchParams.get('status') || 'all';
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [editedStatus, setEditedStatus] = useState<Lead['status']>('new');
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, [filter]);

  // Open a lead directly when linked to as /leads?lead=<id> (e.g. from the dashboard)
  useEffect(() => {
    const leadParam = searchParams.get('lead');
    if (!leadParam || leads.length === 0) return;
    const lead = leads.find(l => l.id === Number(leadParam));
    if (lead) {
      handleOpenLead(lead);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('lead');
    setSearchParams(next, { replace: true });
  }, [leads]);

  const setFilter = (status: string) => {
    const next = new URLSearchParams(searchParams);
    if (status === 'all') {
      next.delete('status');
    } else {
      next.set('status', status);
    }
    setSearchParams(next, { replace: true });
  };

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') {
        params.append('status', filter);
      }

      const response = await fetch(getApiUrl(`/api/leads?${params.toString()}`), {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
      }
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLead = (lead: Lead) => {
    setSelectedLead(lead);
    setEditedNotes(lead.notes || '');
    setEditedStatus(lead.status);
    setShowDetailModal(true);
  };

  const handleUpdateLead = async () => {
    if (!selectedLead) return;

    setSaving(true);
    try {
      const response = await fetch(getApiUrl(`/api/leads/${selectedLead.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          status: editedStatus,
          notes: editedNotes,
        }),
      });

      if (response.ok) {
        setShowDetailModal(false);
        fetchLeads(); // Refresh the list
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update lead');
      }
    } catch (error) {
      alert('Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  const handleTransferToCRM = async () => {
    if (!selectedLead) return;

    if (!confirm(`Transfer "${selectedLead.company_name}" to CRM?\n\nThis will create a new company, contact, and deal in the CRM system.`)) {
      return;
    }

    setTransferring(true);
    try {
      const response = await fetch(getApiUrl(`/api/crm/transfer/${selectedLead.id}`), {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        alert(`Successfully transferred to CRM!\n\nCompany ID: ${data.crm_data?.company?.id}\nContact ID: ${data.crm_data?.contact?.id}\nDeal ID: ${data.crm_data?.deal?.id}`);
        setShowDetailModal(false);
        fetchLeads(); // Refresh the list
      } else {
        alert(`Failed to transfer to CRM:\n${data.error}\n\n${data.details?.message || ''}`);
      }
    } catch (error) {
      alert(`Failed to transfer to CRM: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setTransferring(false);
    }
  };

  const getStatusBadge = (status: Lead['status']) => {
    const colors = {
      new: 'bg-green-900/50 text-green-400 border-green-700',
      contacted: 'bg-blue-900/50 text-blue-400 border-blue-700',
      quoted: 'bg-yellow-900/50 text-yellow-400 border-yellow-700',
      won: 'bg-purple-900/50 text-purple-400 border-purple-700',
      lost: 'bg-red-900/50 text-red-400 border-red-700',
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium border ${colors[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    quoted: leads.filter(l => l.status === 'quoted').length,
    won: leads.filter(l => l.status === 'won').length,
    lost: leads.filter(l => l.status === 'lost').length,
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Leads</h1>
        <p className="text-dark-text-secondary text-sm sm:text-base">Manage and track leads across all brands</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4 mb-6">
        <div className="card p-4">
          <div className="text-dark-text-secondary text-xs mb-1">Total</div>
          <div className="text-white text-2xl font-bold">{stats.total}</div>
        </div>
        <div className="card p-4">
          <div className="text-green-400 text-xs mb-1">New</div>
          <div className="text-white text-2xl font-bold">{stats.new}</div>
        </div>
        <div className="card p-4">
          <div className="text-blue-400 text-xs mb-1">Contacted</div>
          <div className="text-white text-2xl font-bold">{stats.contacted}</div>
        </div>
        <div className="card p-4">
          <div className="text-yellow-400 text-xs mb-1">Quoted</div>
          <div className="text-white text-2xl font-bold">{stats.quoted}</div>
        </div>
        <div className="card p-4">
          <div className="text-purple-400 text-xs mb-1">Won</div>
          <div className="text-white text-2xl font-bold">{stats.won}</div>
        </div>
        <div className="card p-4">
          <div className="text-red-400 text-xs mb-1">Lost</div>
          <div className="text-white text-2xl font-bold">{stats.lost}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <label className="text-sm font-medium text-dark-text">Filter by status:</label>
          <div className="flex gap-2 flex-wrap">
            {['all', 'new', 'contacted', 'quoted', 'won', 'lost'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-dark-bg text-dark-text hover:bg-slate-700'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-dark-text-secondary">
            Loading leads...
          </div>
        ) : leads.length === 0 ? (
          <div className="p-8 text-center text-dark-text-secondary">
            No leads found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-dark-bg border-b border-dark-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-text-secondary uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-text-secondary uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-text-secondary uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-text-secondary uppercase tracking-wider">
                    Brand
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-text-secondary uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-text-secondary uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-text-secondary uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-border">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-dark-text">{lead.company_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-dark-text">{lead.contact_name}</div>
                      <div className="text-xs text-dark-text-secondary">{lead.email}</div>
                      <div className="text-xs text-dark-text-secondary">{lead.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-dark-text">
                        {lead.paper_size} · {lead.bw_volume.toLocaleString()} B&W · {lead.colour_volume.toLocaleString()} Color
                      </div>
                      {lead.estimated_total && (
                        <div className="text-xs text-dark-text-secondary">
                          Est: £{lead.estimated_total.toFixed(2)}/month
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-dark-text capitalize">{lead.brand_id.replace(/([A-Z])/g, ' $1').trim()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {getStatusBadge(lead.status)}
                        {lead.crm_transferred_at && (
                          <span className="px-2 py-1 rounded text-xs font-medium border bg-purple-900/50 text-purple-400 border-purple-700">
                            ✓ In CRM
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-text-secondary">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleOpenLead(lead)}
                        className="btn btn-secondary text-sm"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lead Detail Modal */}
      {showDetailModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="card p-4 sm:p-6 w-full max-w-3xl my-2 sm:my-0">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white">{selectedLead.company_name}</h2>
                <p className="text-dark-text-secondary">Lead #{selectedLead.id}</p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-dark-text-secondary hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-white mb-3">Contact Information</h3>
                <div>
                  <label className="text-xs text-dark-text-secondary">Contact Name</label>
                  <p className="text-dark-text">{selectedLead.contact_name}</p>
                </div>
                <div>
                  <label className="text-xs text-dark-text-secondary">Email</label>
                  <p className="text-dark-text">{selectedLead.email}</p>
                </div>
                <div>
                  <label className="text-xs text-dark-text-secondary">Phone</label>
                  <p className="text-dark-text">{selectedLead.phone}</p>
                </div>
                {selectedLead.postcode && (
                  <div>
                    <label className="text-xs text-dark-text-secondary">Postcode</label>
                    <p className="text-dark-text">{selectedLead.postcode}</p>
                  </div>
                )}
                {selectedLead.existing_provider && (
                  <div>
                    <label className="text-xs text-dark-text-secondary">Existing Provider</label>
                    <p className="text-dark-text">{selectedLead.existing_provider}</p>
                  </div>
                )}
              </div>

              {/* Requirements */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-white mb-3">Requirements</h3>
                <div>
                  <label className="text-xs text-dark-text-secondary">Vertical</label>
                  <p className="text-dark-text capitalize">{selectedLead.vertical || 'copiers'}</p>
                </div>
                <div>
                  <label className="text-xs text-dark-text-secondary">Paper Size</label>
                  <p className="text-dark-text">{selectedLead.paper_size}</p>
                </div>
                <div>
                  <label className="text-xs text-dark-text-secondary">Monthly Volume</label>
                  <p className="text-dark-text">
                    {selectedLead.bw_volume.toLocaleString()} B&W · {selectedLead.colour_volume.toLocaleString()} Colour
                  </p>
                </div>
                {selectedLead.estimated_total && (
                  <div>
                    <label className="text-xs text-dark-text-secondary">Estimated Total</label>
                    <p className="text-dark-text text-lg font-bold">£{selectedLead.estimated_total.toFixed(2)}/month</p>
                  </div>
                )}
                <div>
                  <label className="text-xs text-dark-text-secondary">Brand</label>
                  <p className="text-dark-text capitalize">{selectedLead.brand_id.replace(/([A-Z])/g, ' $1').trim()}</p>
                </div>
              </div>
            </div>

            {/* Marketing Info */}
            {(selectedLead.utm_source || selectedLead.referrer) && (
              <div className="mb-6 p-4 bg-dark-bg rounded-lg">
                <h3 className="text-sm font-bold text-white mb-2">Marketing Source</h3>
                <div className="grid md:grid-cols-3 gap-4 text-xs">
                  {selectedLead.utm_source && (
                    <div>
                      <label className="text-dark-text-secondary">Source</label>
                      <p className="text-dark-text">{selectedLead.utm_source}</p>
                    </div>
                  )}
                  {selectedLead.utm_medium && (
                    <div>
                      <label className="text-dark-text-secondary">Medium</label>
                      <p className="text-dark-text">{selectedLead.utm_medium}</p>
                    </div>
                  )}
                  {selectedLead.utm_campaign && (
                    <div>
                      <label className="text-dark-text-secondary">Campaign</label>
                      <p className="text-dark-text">{selectedLead.utm_campaign}</p>
                    </div>
                  )}
                  {selectedLead.referrer && (
                    <div className="md:col-span-3">
                      <label className="text-dark-text-secondary">Referrer</label>
                      <p className="text-dark-text truncate">{selectedLead.referrer}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* CRM Fields */}
            <div className="space-y-4 mb-6">
              <h3 className="text-lg font-bold text-white">CRM Management</h3>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">
                  Status
                </label>
                <select
                  value={editedStatus}
                  onChange={(e) => setEditedStatus(e.target.value as Lead['status'])}
                  className="input w-full"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="quoted">Quoted</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">
                  Notes
                </label>
                <textarea
                  value={editedNotes}
                  onChange={(e) => setEditedNotes(e.target.value)}
                  className="input w-full min-h-[150px]"
                  placeholder="Add notes about this lead..."
                />
              </div>
            </div>

            {/* CRM Transfer Section */}
            {selectedLead.crm_transferred_at ? (
              <div className="bg-green-900/30 border border-green-700 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-bold text-green-400 mb-2">✓ Transferred to CRM</h3>
                <div className="text-xs text-dark-text space-y-1">
                  <p>Date: {new Date(selectedLead.crm_transferred_at).toLocaleString()}</p>
                  {selectedLead.crm_company_id && <p>Company ID: {selectedLead.crm_company_id}</p>}
                  {selectedLead.crm_contact_id && <p>Contact ID: {selectedLead.crm_contact_id}</p>}
                  {selectedLead.crm_deal_id && <p>Deal ID: {selectedLead.crm_deal_id}</p>}
                </div>
              </div>
            ) : selectedLead.crm_transfer_error ? (
              <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-bold text-red-400 mb-2">⚠ Transfer Failed</h3>
                <div className="text-xs text-dark-text">
                  <p>{JSON.parse(selectedLead.crm_transfer_error).error || 'Unknown error'}</p>
                </div>
              </div>
            ) : null}

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-between">
              <div>
                {!selectedLead.crm_transferred_at && (
                  <button
                    onClick={handleTransferToCRM}
                    disabled={transferring || saving}
                    className="btn bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto"
                  >
                    {transferring ? 'Transferring...' : '🚀 Transfer to CRM'}
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="btn btn-secondary flex-1 sm:flex-none"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateLead}
                  disabled={saving || transferring}
                  className="btn btn-primary flex-1 sm:flex-none"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
